import {
  CoverageNeed,
  NeedName,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  AkaAdviceCheckboxes,
  AkaAdvicePriceFormValues,
} from "../../interfaces";

import { StyledAlert, StyledRow } from "App/global-style";
import CollapsibleContainer from "Components/CollapsibleContainer";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { DetailsCardWrapper } from "Components/InsuranceDetailsCard/styled";
import { VALUE_TYPE } from "Components/NumberValue";
import { AdvicePlotCriticalIllness } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/AdvicePlots/CriticalIllness/AdvicePlotCriticalIllness";
import { AlreadyInsured } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/AlreadyInsured";
import { ProductCoverages } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/ProductCoverages";
import {
  getPurchaseFromAdvisorMessage,
  replaceUserSupportNumberInText,
} from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/utils";
import { useAdviceDetailsFormValues } from "Features/CheckPrice/Products/Advice/ProductPrice/hooks/stateSelectors";
import { mapInsuranceNameToAdviceCoverageName } from "Features/CheckPrice/Products/Advice/ProductPrice/utils";
import { Description } from "Features/CheckPrice/Products/Advice/adviceStyled";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import {
  getPaymentRestriction,
  isNotAllowed,
} from "Store/akaAdvice/interfaces/getters";
import { akaAdvicePriceCalculationRequestStart } from "Store/akaAdvice/slice";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import {
  useIsAkademikerneBranding,
  usePartnerId,
} from "Store/partners/partnerHooks";
import { TextLanguage } from "Store/texts/interfaces";
import { FormikProps } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import {
  checkShouldPurchaseFromAdvisor,
  displayCanGetHigherFromAdvisor,
} from "../../../utils";
import { AdviceCardComment } from "../../AdviceCardComment";
import { AdviceCardHeader } from "../../AdviceCardHeader";
import { InsurancePrice } from "../../AdvicePrice/InsurancePrice";
import { AdvisorUwInfo } from "../../AdvisorUwInfo";
import { CloseToMaxAgeWarning } from "../../CloseToMaxAgeWarning";
import { usePriceStatus } from "../../hooks/usePriceStatus";
import { ContentWrapper } from "../../styled";
import { getIndividualAdvicePlotRange } from "../../utils";
import { IndividualCriticalIllnessForm } from "../Forms/IndividualCriticalIllnessForm";
import { CriticalIllnessAdviceText } from "../Texts/CriticalIllnessAdviceText";
import {
  CRITICAL_ILLNESS_END_AGE,
  CRITICAL_ILLNESS_MAX_AGE,
} from "../criticalIllnessAdviceCardConstants";

export interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  adviceCard: any;
  lang: TextLanguage;
}

export const CriticalIllnessAdviceCard = ({
  formProps,
  need,
  adviceCard,
  lang,
}: Props): React.ReactElement | null => {
  const isAkaBranding = useIsAkademikerneBranding();
  const partnerId = usePartnerId();
  const dispatch = useDispatch();
  const adviceDetails = useAdviceDetailsFormValues();
  const isAdvisor = useIsAdvisorSelector();
  const selected = formProps.values.criticalIllnessCheckbox;
  const currentSliderValue = formProps.values.criticalIllnessInsuredSumSlider;
  const {
    priceIsChanging,
    setPriceIsChanging,
    priceIsLoading,
    setPriceIsLoading,
  } = usePriceStatus();
  const insuranceType: string = adviceCard.insuranceType[lang];

  const recalculatePrice = (
    formValues: AkaAdvicePriceFormValues,
    specificProduct: ProductName,
  ): void => {
    setPriceIsChanging(false);
    setPriceIsLoading(true);
    dispatch(
      akaAdvicePriceCalculationRequestStart({
        isCollectiveAdvice: isAkaBranding,
        priceFormValues: formValues,
        specificProduct,
      }),
    );
  };

  if (
    !adviceDetails ||
    isNotAllowed(need, ProductCoverageName.CRITICAL_ILLNESS)
  ) {
    return null;
  }

  const paymentRestriction = getPaymentRestriction(
    need,
    ProductCoverageName.CRITICAL_ILLNESS,
  );
  const shouldPurchaseFromAdvisor = checkShouldPurchaseFromAdvisor(
    isAdvisor,
    need,
    ProductCoverageName.CRITICAL_ILLNESS,
  );

  const plotRange = getIndividualAdvicePlotRange(
    isAdvisor,
    adviceDetails.income,
    paymentRestriction,
  );

  return (
    <div className="AdviceCard_CRITICAL_ILLNESS">
      <DetailsCardWrapper
        key={insuranceType}
        adviceDetailsCard
        selected={selected}
      >
        <StyledRow>
          <AdviceCardHeader
            insuranceId={AkaAdviceCheckboxes.CRITICAL_ILLNESS_CHECKBOX}
            insuranceType={insuranceType}
          />
          <InsurancePrice
            needName={NeedName.CRITICAL_ILLNESS}
            productCoverages={[ProductCoverageName.CRITICAL_ILLNESS]}
            priceIsChanging={priceIsChanging}
            priceIsLoading={priceIsLoading}
          />
        </StyledRow>
        <ContentWrapper className="AdviceCardContent">
          <div className="AdviceHeaderText">
            <Description fw={600} size="16px">
              Alvorlig sykdom eller langvarig sykehusopphold kan medføre store
              økonomiske konsekvenser. Gir deg raskt en skattefri
              engangsutbetaling som kan være til god hjelp gjennom en vanskelig
              periode.
            </Description>
          </div>
          <Description fw={700} size="16px" className="AdviceHeaderText">
            Tilpass engangsutbetaling
          </Description>
          <CriticalIllnessAdviceText
            need={need}
            adviceTexts={{
              ...adviceCard.adviceTexts,
              adviceUnavailableMessages: adviceCard.adviceUnavailableMessages,
            }}
            lang={lang}
          />
          <CollapsibleContainer isOpen={selected}>
            <AdvicePlotCriticalIllness
              income={adviceDetails.income}
              payout={currentSliderValue ?? 0}
              valueType={VALUE_TYPE.NOK}
              advicePlot={adviceCard.advicePlot}
              lang={lang}
              range={plotRange}
            />
            <IndividualCriticalIllnessForm
              formProps={formProps}
              need={need}
              recalculatePrice={recalculatePrice}
              setPriceIsChanging={setPriceIsChanging}
              adviceCard={adviceCard}
              lang={lang}
            />
            <CloseToMaxAgeWarning
              need={need}
              coverageName={ProductCoverageName.CRITICAL_ILLNESS}
              warningTexts={adviceCard.warnings}
              lang={lang}
              warningVariables={{
                maxAge: CRITICAL_ILLNESS_MAX_AGE,
                endAge: CRITICAL_ILLNESS_END_AGE,
              }}
            />

            {isAdvisor && (
              <AdvisorUwInfo
                insuranceName={INSURANCE_NAME_VALUE.CRITICAL_ILLNESS}
                sliderValue={currentSliderValue ?? 0}
                paymentRestriction={paymentRestriction}
                uwInfoTexts={adviceCard.advisorTexts.uwInfoTexts}
                lang={lang}
              />
            )}
            {displayCanGetHigherFromAdvisor(
              currentSliderValue ?? 0,
              need,
              ProductCoverageName.CRITICAL_ILLNESS,
              isAdvisor,
            ) && (
              <StyledAlert
                className="PurchaseFromAdvisorText"
                title={replaceUserSupportNumberInText(
                  partnerId,
                  adviceCard.adviceUnavailableMessages.purchaseFromAdvisor
                    .userSupportNumber,
                  adviceCard.adviceUnavailableMessages.purchaseFromAdvisor
                    .maxPayoutCustomer[lang],
                  lang,
                )}
              />
            )}
            {shouldPurchaseFromAdvisor && (
              <StyledAlert
                className="PurchaseFromAdvisorText"
                title={getPurchaseFromAdvisorMessage(
                  isAdvisor,
                  partnerId,
                  need,
                  ProductCoverageName.CRITICAL_ILLNESS,
                  adviceCard.adviceUnavailableMessages.purchaseFromAdvisor,
                  lang,
                )}
              />
            )}

            <AlreadyInsured insuranceType={insuranceType} />
            <ProductCoverages
              selected={selected}
              readableInsuranceType={insuranceType}
              backendInsuranceName={ProductBackendName.CRITICAL_ILLNESS}
              coverageGroup={mapInsuranceNameToAdviceCoverageName(
                INSURANCE_NAME_VALUE.CRITICAL_ILLNESS,
              )}
              description={adviceCard.adviceTexts.productCoveragesDesc[lang]}
            />
          </CollapsibleContainer>
          <AdviceCardComment
            commentId="criticalIllnessAdviceComment"
            formProps={formProps}
            label="Notater om anbefaling og kundens valg av kritisk sykdom"
          />
        </ContentWrapper>
      </DetailsCardWrapper>
    </div>
  );
};
